import { useEffect, useRef, type ReactNode } from 'react'

import { useSessionStore } from '../auth/session-store'

import { RealtimeContext } from './context'
import { realtimeManager } from './instance'
import type { RealtimeManager } from './manager'

export interface RealtimeProviderProps {
  children: ReactNode
  /** Test seam; production always uses the singleton above. */
  manager?: RealtimeManager
  /** Blocking startup gates (update/terms) must not hold a socket open. */
  suspended?: boolean
}

/**
 * Opens exactly one realtime connection after the session is restored and
 * closes/clears it on logout, account deletion or an expired session.
 *
 * Token refreshes that keep the same user do not restart the socket; a new
 * user id or a guest state does. Screens never call `start`/`stop` themselves.
 */
export function RealtimeProvider({
  children,
  manager = realtimeManager,
  suspended = false,
}: RealtimeProviderProps) {
  const authenticated = useSessionStore(
    (state) => state.status === 'authenticated' && Boolean(state.accessToken),
  )
  const accessToken = useSessionStore((state) => state.accessToken)
  const userId = useSessionStore((state) => state.user?.id ?? null)
  const activeUserId = useRef<string | null>(null)
  const canConnect = authenticated && !suspended

  useEffect(() => {
    if (!canConnect) {
      activeUserId.current = null
      manager.stop()
      return
    }
    if (activeUserId.current !== null && activeUserId.current !== userId) {
      manager.stop()
    }
    activeUserId.current = userId
    manager.start()
  }, [canConnect, manager, userId])

  // A token refresh after a 4401 is the only automatic retry path: auth
  // closes stay stopped until the in-memory session actually changes.
  useEffect(() => {
    if (!canConnect) return
    if (manager.getStatus() === 'closed') manager.start()
  }, [accessToken, canConnect, manager])

  useEffect(() => {
    return () => {
      manager.stop()
    }
  }, [manager])

  return (
    <RealtimeContext.Provider value={manager}>
      {children}
    </RealtimeContext.Provider>
  )
}
